import { CONTEXT, TAG, UNIVERSAL } from "./der";

// Everything written here is freshly allocated, which is what Web Crypto asks of anything it is handed to sign.
type Bytes = Uint8Array<ArrayBuffer>;

// The other direction from `der.ts`: what a certificate is built out of. It is here rather than in a library because
// the page already reads ASN.1 and two implementations of one encoding on one page is the copy that stops agreeing.

export function sequence(items: Uint8Array[]): Bytes {
  return element(UNIVERSAL, TAG.sequence, true, concat(items));
}

export function set(items: Uint8Array[]): Bytes {
  return element(UNIVERSAL, TAG.set, true, concat(items));
}

// An unsigned number written the one way DER allows: as few bytes as it fits in, behind a zero where the top bit
// of the first would otherwise read as a minus sign. A byte an integer did not need is one OpenSSL refuses over.
export function integer(value: Uint8Array | number): Bytes {
  const bytes = typeof value === "number" ? fromNumber(value) : value;
  let start = 0;
  while (start < bytes.length - 1 && bytes[start] === 0) start += 1;
  const trimmed = bytes.subarray(start);
  const body = (trimmed[0] & 0x80) === 0 ? trimmed : concat([new Uint8Array([0]), trimmed]);
  return element(UNIVERSAL, TAG.integer, false, body);
}

export function bitString(bytes: Uint8Array): Bytes {
  // Nothing written here is ever a partial byte, so the count of unused bits at the front is always zero.
  return element(UNIVERSAL, TAG.bitString, false, concat([new Uint8Array([0]), bytes]));
}

// A BIT STRING of named flags, where DER counts from the top of the first byte, drops every trailing zero bit, and
// says in the leading byte how many of the last one to ignore — which is why the highest flag set decides the length.
export function namedBits(indexes: number[]): Bytes {
  const highest = Math.max(...indexes);
  const size = Math.floor(highest / 8) + 1;
  const bytes = new Uint8Array(size + 1);
  bytes[0] = size * 8 - (highest + 1);
  for (const index of indexes) bytes[1 + (index >> 3)] |= 0x80 >> (index & 7);
  return element(UNIVERSAL, TAG.bitString, false, bytes);
}

export function octetString(bytes: Uint8Array): Bytes {
  return element(UNIVERSAL, TAG.octetString, false, bytes);
}

export function boolean(value: boolean): Bytes {
  // DER spells true as every bit set, where BER would take any non-zero byte for it.
  return element(UNIVERSAL, TAG.boolean, false, new Uint8Array([value ? 0xff : 0x00]));
}

export function nul(): Bytes {
  return element(UNIVERSAL, 5, false, new Uint8Array());
}

export function oid(dotted: string): Bytes {
  const arcs = dotted.split(".").map((arc) => BigInt(arc));
  if (arcs.length < 2) throw new Error(`Not an object identifier: ${dotted}`);
  // The first two arcs share one subidentifier, arcs 0 and 1 holding forty children each and arc 2 the rest.
  const parts = [arcs[0] * 40n + arcs[1], ...arcs.slice(2)];
  return element(UNIVERSAL, TAG.oid, false, concat(parts.map(base128)));
}

export function utf8String(text: string): Bytes {
  return element(UNIVERSAL, 12, false, new TextEncoder().encode(text));
}

// The one string type RFC 5280 asks for by name, and the one a country code has to be written in.
export function printableString(text: string): Bytes {
  return element(UNIVERSAL, 19, false, new TextEncoder().encode(text));
}

// A tag with the value still written inside it, which is how a version, a set of extensions and a curve are carried.
export function explicit(tag: number, content: Uint8Array): Bytes {
  return element(CONTEXT, tag, true, content);
}

// A tag written over the value's own, which is how every name in a GeneralNames and every field of an AKI is carried.
export function implicit(tag: number, content: Uint8Array, constructed = false): Bytes {
  return element(CONTEXT, tag, constructed, content);
}

// UTCTime writes the year with two digits and RFC 5280 reads 50 and above as the twentieth century, so a certificate
// reaching 2050 has to be written the other way — and RFC 5280 requires seconds and `Z` in both.
export function time(date: Date): Bytes {
  const year = date.getUTCFullYear();
  const digits = [
    date.getUTCMonth() + 1,
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  ].map(pad).join("");
  const generalized = year >= 2050;
  const text = `${generalized ? String(year) : pad(year % 100)}${digits}Z`;
  return element(UNIVERSAL, generalized ? TAG.generalizedTime : TAG.utcTime, false, new TextEncoder().encode(text));
}

function concat(parts: Uint8Array[]): Bytes {
  const total = parts.reduce((size, part) => size + part.length, 0);
  const joined = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    joined.set(part, offset);
    offset += part.length;
  }
  return joined;
}

function element(cls: number, tag: number, constructed: boolean, content: Uint8Array): Bytes {
  if (tag > 30) throw new Error(`Tag ${tag} is past the one-byte form`);
  const first = (cls << 6) | (constructed ? 0x20 : 0) | tag;
  return concat([new Uint8Array([first]), length(content.length), content]);
}

// Under 128 the length is the byte; past it, a count of bytes with the top bit set, then the length big-endian.
function length(size: number): Uint8Array {
  if (size < 0x80) return new Uint8Array([size]);
  const bytes: number[] = [];
  for (let rest = size; rest > 0; rest = Math.floor(rest / 256)) bytes.unshift(rest % 256);
  return new Uint8Array([0x80 | bytes.length, ...bytes]);
}

function base128(value: bigint): Uint8Array {
  const bytes: number[] = [Number(value % 128n)];
  for (let rest = value / 128n; rest > 0n; rest /= 128n) bytes.unshift(Number(rest % 128n) | 0x80);
  return new Uint8Array(bytes);
}

function fromNumber(value: number): Uint8Array {
  const bytes: number[] = [];
  for (let rest = value; rest > 0; rest = Math.floor(rest / 256)) bytes.unshift(rest % 256);
  return new Uint8Array(bytes.length > 0 ? bytes : [0]);
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}
